"use client"

import React from "react"
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query"
import axios from "axios"
import Sidebar from "../../components/Sidebar"
import Topbar from "../../components/Topbar"
import { FiEdit2, FiTrash2, FiPlus } from "react-icons/fi"
import toast, { Toaster } from "react-hot-toast"
import { Link } from "react-router-dom"
import { Modal, Button, Form } from "react-bootstrap"
import ConfirmModal from "../../components/ConfirmModal"

const Shifts = () => {
    const baseUrl = process.env.REACT_APP_APIURL
    const queryClient = useQueryClient()
    const [showModal, setShowModal] = React.useState(false)
    const [showDeleteModal, setShowDeleteModal] = React.useState(false)
    const [selectedShift, setSelectedShift] = React.useState(null)
    const [isEditing, setIsEditing] = React.useState(false)

    const [formData, setFormData] = React.useState({
        name: "",
        startTime: "",
        endTime: "",
    })

    // Fetch all shifts
    const {
        data: apiResponse,
        isLoading,
    } = useQuery({
        queryKey: ["shift-list"],
        queryFn: async () => {
            const res = await axios.get(`${baseUrl}api/shift/all`)
            return res.data
        },
    })

    // Mutations
    const createShiftMutation = useMutation({
        mutationFn: async (data) => axios.post(`${baseUrl}api/shift/create`, data),
        onSuccess: () => {
            toast.success("Shift created successfully!")
            queryClient.invalidateQueries({ queryKey: ["shift-list"] })
            handleCloseModal()
        },
        onError: (error) => toast.error(error?.response?.data?.message || "Failed to create shift."),
    })

    const updateShiftMutation = useMutation({
        mutationFn: async ({ id, data }) => axios.put(`${baseUrl}api/shift/update/${id}`, data),
        onSuccess: () => {
            toast.success("Shift updated successfully!")
            queryClient.invalidateQueries({ queryKey: ["shift-list"] })
            handleCloseModal()
        },
        onError: (error) => toast.error(error?.response?.data?.message || "Failed to update shift."),
    })

    const deleteShiftMutation = useMutation({
        mutationFn: async (id) => axios.delete(`${baseUrl}api/shift/delete/${id}`),
        onSuccess: () => {
            toast.success("Shift deleted successfully!")
            queryClient.invalidateQueries({ queryKey: ["shift-list"] })
            setShowDeleteModal(false)
            setSelectedShift(null)
        },
        onError: (error) => toast.error(error?.response?.data?.message || "Failed to delete shift."),
    })

    const handleOpenModal = (shift = null) => {
        if (shift) {
            setIsEditing(true)
            setSelectedShift(shift)
            setFormData({
                name: shift.name,
                startTime: shift.startTime,
                endTime: shift.endTime,
            })
        } else {
            setIsEditing(false)
            setFormData({ name: "", startTime: "", endTime: "" })
        }
        setShowModal(true)
    }

    const handleCloseModal = () => {
        setShowModal(false)
        setSelectedShift(null)
        setFormData({ name: "", startTime: "", endTime: "" })
    }

    const handleSubmit = (e) => {
        e.preventDefault()
        if (isEditing) {
            updateShiftMutation.mutate({ id: selectedShift._id, data: formData })
        } else {
            createShiftMutation.mutate(formData)
        }
    }

    const handleDeleteClick = (shift) => {
        setSelectedShift(shift)
        setShowDeleteModal(true)
    }

    const handleConfirmDelete = () => {
        if (selectedShift) {
            deleteShiftMutation.mutate(selectedShift._id)
        }
    }

    const formatTime = (timeString) => {
        if (!timeString) return "N/A"
        try {
            const [hours, minutes] = timeString.split(":")
            const h = parseInt(hours)
            const ampm = h >= 12 ? "PM" : "AM"
            const formattedHours = h % 12 || 12
            return `${formattedHours}:${minutes} ${ampm}`
        } catch (e) {
            return timeString
        }
    }

    const shifts = apiResponse?.data || []

    return (
        <>
            <Toaster position="top-right" reverseOrder={false} />
            <Topbar />
            <Sidebar />
            <main id="main" className="main">
                <div className="pagetitle">
                    <h1>Shift Management</h1>
                    <nav>
                        <ol className="breadcrumb">
                            <li className="breadcrumb-item">
                                <Link to="/">Dashboard</Link>
                            </li>
                            <li className="breadcrumb-item active">Shifts</li>
                        </ol>
                    </nav>
                </div>

                <section className="section">
                    <div className="row">
                        <div className="col-lg-12">
                            <div className="card shadow-sm">
                                <div className="card-body">
                                    <div className="d-flex justify-content-between align-items-center pt-3">
                                        <h5 className="card-title p-0 m-0">Shifts List</h5>
                                        <button className="btn btn-sm btn-primary d-inline-flex align-items-center gap-2" onClick={() => handleOpenModal()}>
                                            <FiPlus size={16} /> Add Shift
                                        </button>
                                    </div>
                                    <hr />

                                    {isLoading ? (
                                        <div className="text-center p-5">
                                            <div className="spinner-border text-primary" role="status">
                                                <span className="visually-hidden">Loading...</span>
                                            </div>
                                        </div>
                                    ) : shifts.length === 0 ? (
                                        <div className="alert alert-info text-center">No shifts found.</div>
                                    ) : (
                                        <div className="table-responsive">
                                            <table className="table table-striped table-hover table-bordered align-middle">
                                                <thead className="table-light">
                                                    <tr>
                                                        <th>#</th>
                                                        <th>Shift Name</th>
                                                        <th>Start Time</th>
                                                        <th>End Time</th>
                                                        <th className="text-center">Actions</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {shifts.map((shift, index) => (
                                                        <tr key={shift._id}>
                                                            <td>{index + 1}</td>
                                                            <td className="fw-semibold">{shift.name} ({shift.productCount || 0})</td>
                                                            <td>{formatTime(shift.startTime)}</td>
                                                            <td>{formatTime(shift.endTime)}</td>
                                                            <td className="text-center">
                                                                <button className="btn btn-sm btn-outline-primary me-2" onClick={() => handleOpenModal(shift)}>
                                                                    <FiEdit2 size={14} />
                                                                </button>
                                                                <button className="btn btn-sm btn-outline-danger" onClick={() => handleDeleteClick(shift)}>
                                                                    <FiTrash2 size={14} />
                                                                </button>
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </section>
            </main>

            {/* Add/Edit Modal */}
            <Modal show={showModal} onHide={handleCloseModal}>
                <Modal.Header closeButton>
                    <Modal.Title>{isEditing ? "Edit Shift" : "Add New Shift"}</Modal.Title>
                </Modal.Header>
                <Form onSubmit={handleSubmit}>
                    <Modal.Body>
                        <Form.Group className="mb-3">
                            <Form.Label>Shift Name</Form.Label>
                            <Form.Control
                                type="text"
                                placeholder="e.g. Night Shift"
                                value={formData.name}
                                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                required
                            />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>Start Time</Form.Label>
                            <Form.Control
                                type="time"
                                value={formData.startTime}
                                onChange={(e) => setFormData({ ...formData, startTime: e.target.value })}
                                required
                            />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>End Time</Form.Label>
                            <Form.Control
                                type="time"
                                value={formData.endTime}
                                onChange={(e) => setFormData({ ...formData, endTime: e.target.value })}
                                required
                            />
                        </Form.Group>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={handleCloseModal}>
                            Cancel
                        </Button>
                        <Button variant="primary" type="submit" disabled={createShiftMutation.isLoading || updateShiftMutation.isLoading}>
                            {isEditing ? "Update Shift" : "Save Shift"}
                        </Button>
                    </Modal.Footer>
                </Form>
            </Modal>

            {/* Delete Confirmation Modal */}
            <ConfirmModal
                show={showDeleteModal}
                onHide={() => setShowDeleteModal(false)}
                onConfirm={handleConfirmDelete}
                title="Delete Shift"
                message={`Are you sure you want to delete the shift "${selectedShift?.name}"?`}
            />
        </>
    )
}

export default Shifts
